import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, Check, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useOutletContext, useSearchParams } from 'react-router'

import { alta, subirDocumentoDelAlta, subirImagenDelAlta, subirProductosDelAlta, usePlantillas } from '../../api/alta'
import { useEspacio, useSaldo } from '../../api/consultas'
import { importacion, urlDeLaPlantillaDelEspacio } from '../../api/importacion'
import { maximoPorFicheroDe } from '../../api/medios'
import type { ErrorDeLaApi } from '../../api/nucleo'
import type { Contexto } from '../Modulo'
import { Aviso, Boton, Cargando, Titulo, clases } from '../../componentes/ui'
import { DocumentosSinLeer } from './DocumentosSinLeer'
import {
  agruparPorFichero,
  type AltaEnEspera,
  altDe,
  campoDelFallo,
  conMensaje,
  DATOS_INICIALES,
  type Datos,
  type DocumentoSinLeer,
  enParalelo,
  type Ficheros,
  FICHEROS_INICIALES,
  guardar,
  guardarEspera,
  hayFicherosElegidos,
  type Imagen,
  leerEspera,
  leerGuardado,
  mediosDeLasSubidas,
  mensajeDelFallo,
  olvidar,
  olvidarEspera,
  type Paso,
  pasoDelCampo,
  pasoDeLaDireccion,
  pasosPara,
  ROTULOS,
  sinDocumento,
  yaHabiaEmpezado,
} from './estado'
import { PasoMarca, PasoMateriales, PasoPaginas, PasoPlantilla, PasoProductos, PasoResumen, PasoTipo, type ProductosDelAlta } from './pasos'


const NO_CIERRES = 'No cierres esta pestaña hasta que termine.'

/**
 * El asistente de alta (docs/alta-de-web-con-plantillas.md §8): un paso por pantalla,
 * el progreso arriba, «atrás» siempre. No crea nada en el núcleo hasta «Crear mi
 * web»; entonces crea la web, sube las imágenes y los documentos a ella y pide que se
 * monte el resto, y lleva a la pantalla de progreso.
 *
 * Si algún documento no se ha podido leer, **no sigue**: la web ya existe y el alta espera
 * los materiales, así que se enseña cada documento con lo que dijo el núcleo y se deja subir
 * otro, pegar su texto o quitarlo (DocumentosSinLeer.tsx), y se continúa cuando el cliente lo
 * decide. Si siguiera sola, el mensaje —«pega su texto o súbelo en otro formato»— pediría algo
 * que ya no se podría hacer.
 */
export const Alta = () => {
  const { espacio, permisos } = useOutletContext<Contexto>()
  const navigate = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const { data: cuenta } = useEspacio(espacio)
  // El saldo, para decirlo en el resumen: el alta escribe los textos y el diseño con créditos (plan del CMS dinámico §9).
  const { data: saldo } = useSaldo(espacio)
  const plan = cuenta?.plan && typeof cuenta.plan === 'object' ? cuenta.plan : null
  const planConTienda = (plan?.modulos ?? []).includes('productos')

  const [guardado] = useState(() => leerGuardado(espacio))
  const [datos, setDatos] = useState<Datos>(() => guardado ?? DATOS_INICIALES)
  // Lo escrito se ha recuperado de una visita anterior, pero los ficheros no se pueden guardar: se dice.
  const [recuperado] = useState(() => guardado !== null && yaHabiaEmpezado(guardado))
  const [ficheros, setFicheros] = useState<Ficheros>(FICHEROS_INICIALES)
  const [productos, setProductos] = useState<ProductosDelAlta | null>(null)
  const [analizando, setAnalizando] = useState(false)
  const [errorDeProductos, setErrorDeProductos] = useState<null | string>(null)
  // El paso va en la dirección: «Atrás» del navegador vuelve al paso anterior, no fuera del asistente.
  const [parametros, setParametros] = useSearchParams()
  const [error, setError] = useState<null | { texto: string; enlace?: { href: string; etiqueta: string } }>(null)
  const [creando, setCreando] = useState<null | string>(null)
  // La web creada que espera a que el cliente decida qué hacer con los documentos que no se leyeron.
  const [espera, setEspera] = useState<AltaEnEspera | null>(() => leerEspera(espacio))
  const { data: plantillas, isPending: cargandoPlantillas } = usePlantillas(datos.tipo)
  const plantilla = plantillas?.find((p) => p.slug === datos.plantilla)

  useEffect(() => guardar(espacio, datos), [espacio, datos])
  useEffect(() => {
    if (espera) guardarEspera(espacio, espera)
  }, [espacio, espera])

  // Antes de salir con ficheros elegidos, o mientras se sube lo elegido, el navegador pregunta:
  // lo elegido no se puede guardar, y una subida a medias deja la web sin sus fotos.
  const conFicheros = hayFicherosElegidos(ficheros, productos !== null)
  const protegerLaSalida = creando !== null || (conFicheros && !espera)
  useEffect(() => {
    if (!protegerLaSalida) return
    const alSalir = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', alSalir)
    return () => window.removeEventListener('beforeunload', alSalir)
  }, [protegerLaSalida])

  const cambiar = (parte: Partial<Datos>) => setDatos((d) => ({ ...d, ...parte }))
  const cambiarFicheros = (parte: Partial<Ficheros>) => setFicheros((f) => ({ ...f, ...parte }))

  // El Excel de productos se analiza ya, con el espacio (la web aún no existe); se importa al final, cuando exista.
  const analizarProductos = async (archivo: File) => {
    setErrorDeProductos(null)
    setAnalizando(true)
    try {
      const analisis = await importacion.analizar(archivo, { cliente: espacio })
      setProductos({ archivo, analisis, mapeo: analisis.mapeo })
    } catch (e) {
      setErrorDeProductos((e as ErrorDeLaApi).message || 'No se ha podido leer el Excel.')
    } finally {
      setAnalizando(false)
    }
  }

  // Crear una web es del dueño de la cuenta (§4: cuántas, lo dice el plan).
  if (!permisos.esDueno) {
    if (permisos.cargando) return <Cargando que="Leyendo tus permisos" />
    return <Aviso tono="aviso">Solo el propietario del espacio crea webs. Pídeselo a quien lo sea.</Aviso>
  }

  const pasos = pasosPara(datos.tipo)
  const puedeSeguir: Record<Paso, boolean> = {
    tipo: datos.tipo !== null,
    plantilla: datos.plantilla !== null,
    marca: datos.nombre.trim().length >= 2 && datos.slug.length > 0,
    materiales: true,
    paginas: true,
    productos: productos === null || productos.mapeo.some((m) => m.campo === 'nombre'),
    resumen: true,
  }
  const paso = pasoDeLaDireccion(parametros.get('paso'), pasos, (p) => puedeSeguir[p])
  const indice = pasos.indexOf(paso)
  const setPaso = (p: Paso) => setParametros({ paso: p })
  const siguiente = () => {
    setError(null)
    if (indice < pasos.length - 1) setPaso(pasos[indice + 1]!)
  }
  const atras = () => {
    setError(null)
    if (indice > 0) setPaso(pasos[indice - 1]!)
  }

  /** A la pantalla de progreso de la web ya creada: el asistente no tiene nada más que guardar. */
  const irAlProgreso = (sitio: number, estado: { avisos: string[] }) => {
    olvidar(espacio)
    olvidarEspera(espacio)
    setEspera(null)
    void clienteDeConsultas.invalidateQueries({ queryKey: ['sitios', espacio] })
    // Sustituyendo al asistente en el historial: «Atrás» desde el progreso no vuelve a abrirlo.
    navigate(`/e/${espacio}/s/${sitio}/alta`, { replace: true, state: { avisos: estado.avisos } })
  }

  /**
   * Monta la web con lo que ya está subido y lleva al progreso. Los productos ya están en el
   * núcleo (se subieron antes): los importa el propio alta, en su paso, antes de los menús.
   */
  const terminar = async ({ sitio, medios, avisos }: AltaEnEspera) => {
    try {
      setCreando('Montando la web…')
      await alta.continuar(sitio, medios)
      irAlProgreso(sitio, { avisos })
    } catch (e) {
      // Continuar ha fallado (o el alta ya estaba en marcha): la pantalla de progreso sabe qué hacer.
      // Sin créditos para la IA (402) no hace falta el aviso: el progreso enseña lo que cuesta y las dos salidas.
      const sinSaldo = (e as Partial<ErrorDeLaApi>)?.status === 402
      irAlProgreso(sitio, { avisos: sinSaldo ? avisos : [...avisos, mensajeDelFallo(e)] })
    }
  }

  /** Otro fichero, o el texto pegado, en lugar de un documento que no se leyó. */
  const leerOtraVez = async (id: number, fichero: File): Promise<boolean> => {
    if (!espera) return false
    try {
      await subirDocumentoDelAlta(espera.sitio, fichero)
      setEspera((e) => (e ? sinDocumento(e, id) : e))
      return true
    } catch (fallo) {
      setEspera((e) => (e ? conMensaje(e, id, (fallo as Error).message) : e))
      return false
    }
  }

  const crear = async () => {
    setError(null)
    setCreando('Creando la web…')
    let sitio: null | number = null
    // Lo que no ha salido bien y no para el alta: la pantalla de progreso los enseña uno a uno.
    const avisos: string[] = []
    try {
      const creado = await alta.iniciar({
        cliente: espacio,
        tipo: datos.tipo!,
        plantilla: datos.plantilla!,
        nombre: datos.nombre.trim(),
        slug: datos.slug,
        ...(datos.dominio ? { dominio: datos.dominio } : {}),
        marca: {
          ...(datos.marca.primario ? { primario: datos.marca.primario } : {}),
          ...(datos.marca.secundario ? { secundario: datos.marca.secundario } : {}),
          ...(datos.marca.acento ? { acento: datos.marca.acento } : {}),
          tipografia: datos.marca.tipografia,
        },
        contacto: {
          ...(datos.contacto.telefono ? { telefono: datos.contacto.telefono } : {}),
          ...(datos.contacto.correo ? { correo: datos.contacto.correo } : {}),
          ...(datos.contacto.direccion.trim() ? { direccion: datos.contacto.direccion.trim() } : {}),
          ...(datos.contacto.horario.trim() ? { horario: datos.contacto.horario.trim() } : {}),
        },
        ...(datos.fiscal.razonSocial || datos.fiscal.identificacion || datos.fiscal.direccion
          ? { fiscal: { ...(datos.fiscal.razonSocial ? { razonSocial: datos.fiscal.razonSocial.trim() } : {}), ...(datos.fiscal.identificacion ? { identificacion: datos.fiscal.identificacion } : {}), ...(datos.fiscal.direccion ? { direccion: datos.fiscal.direccion.trim() } : {}) } }
          : {}),
        ...(datos.tipo === 'tienda' ? { tienda: datos.tienda } : {}),
        brief: datos.brief.trim(),
        paginas: datos.paginas,
        servicios: datos.servicios.split('\n').map((s) => s.trim()).filter(Boolean),
      })
      sitio = creado.sitio

      // Las imágenes, a la web recién creada por la misma puerta que Medios: varias a la vez (tres en paralelo), con el avance a la vista.
      const subidasDe: { logo?: number; logoEnOscuro?: number; favicon?: number; porClave: Record<string, Array<number | undefined>>; sueltas: Array<number | undefined> } = { porClave: {}, sueltas: [] }
      const tareas: Array<{ imagen: Imagen; guardar: (id: number) => void }> = []
      if (ficheros.logo) tareas.push({ imagen: ficheros.logo, guardar: (id) => (subidasDe.logo = id) })
      if (ficheros.logoEnOscuro) tareas.push({ imagen: ficheros.logoEnOscuro, guardar: (id) => (subidasDe.logoEnOscuro = id) })
      if (ficheros.favicon) tareas.push({ imagen: ficheros.favicon, guardar: (id) => (subidasDe.favicon = id) })
      for (const [clave, lista] of Object.entries(ficheros.imagenes)) {
        if (lista.length === 0) continue
        subidasDe.porClave[clave] = new Array<number | undefined>(lista.length)
        lista.forEach((imagen, i) => tareas.push({ imagen, guardar: (id) => (subidasDe.porClave[clave]![i] = id) }))
      }
      // Las sueltas, en el orden en que se eligieron: el alta las coloca en los huecos que se queden sin foto.
      subidasDe.sueltas = new Array<number | undefined>(ficheros.sueltas.length)
      ficheros.sueltas.forEach((imagen, i) => tareas.push({ imagen, guardar: (id) => (subidasDe.sueltas[i] = id) }))
      // El mismo fichero elegido para varios huecos se sube una vez y su medio va a todos
      // (estado.ts): el progreso cuenta subidas de verdad, no huecos.
      const subidasPendientes = agruparPorFichero(tareas)
      const total = subidasPendientes.length
      let subidas = 0
      const progreso = () => setCreando(`${total === 1 ? 'Subiendo la imagen…' : `Subiendo las imágenes (${subidas} de ${total})…`} ${NO_CIERRES}`)
      // Lo que pase del máximo por fichero del plan se reduce en el navegador antes de subirlo.
      const maximoBytes = total > 0 ? await maximoPorFicheroDe(espacio) : null
      if (total > 0) progreso()
      // Una imagen que no sube **no tira las demás**: se apunta y se sigue. Antes, la que
      // fallaba se llevaba por delante todo el paso —las ya subidas se quedaban en Medios
      // ocupando cuota, sin asignar, y la web se montaba sin ninguna imagen—.
      await enParalelo(subidasPendientes, 3, async ({ imagen, tareas: huecos }) => {
        try {
          const id = await subirImagenDelAlta(sitio!, espacio, imagen.fichero, imagen.alt.trim() || altDe(imagen.fichero), {
            maximoBytes,
            alReducir: (nombre) => setCreando(`Reduciendo «${nombre}»…`),
          })
          for (const hueco of huecos) hueco.guardar(id)
        } catch (fallo) {
          avisos.push(`«${imagen.fichero.name}» no se ha podido subir: ${(fallo as Error).message}`)
        }
        subidas++
        progreso()
      })
      // Las que faltan no desplazan a las demás: cada clave conserva el orden que eligió el
      // cliente, y lo que no subió se dice en vez de colocar otra foto en su sitio.
      const medios = mediosDeLasSubidas(subidasDe)

      // El Excel de productos, con el mapeo confirmado: el núcleo lo lee y lo guarda ahora, y el
      // alta importa los productos en su paso, antes de los menús y del diseño. Va antes que los
      // documentos: si alguno no se lee y el alta espera, el Excel ya no depende de esta pestaña.
      if (datos.tipo === 'tienda' && productos) {
        setCreando(`Preparando tus productos… ${NO_CIERRES}`)
        try {
          await subirProductosDelAlta(sitio, productos.archivo, productos.mapeo)
        } catch (e) {
          avisos.push(`Los productos de «${productos.archivo.name}» no se van a importar con la web: ${mensajeDelFallo(e)} Impórtalos después desde Productos → Importar.`)
        }
      }

      // Los documentos del brief: se lee su texto ahora, y es lo que leerá la IA de contenido.
      const sinLeer: DocumentoSinLeer[] = []
      for (const [i, documento] of ficheros.documentos.entries()) {
        setCreando(`Leyendo «${documento.name}»…`)
        try {
          await subirDocumentoDelAlta(sitio, documento)
        } catch (e) {
          sinLeer.push({ id: i, nombre: documento.name, mensaje: (e as Error).message })
        }
      }

      const pendiente: AltaEnEspera = { sitio, medios, avisos, documentos: sinLeer }
      // Uno que no se ha leído para el alta aquí, con la web creada y el alta en «materiales»:
      // el cliente decide qué hacer con él (ver arriba). Lo demás ya está subido.
      if (sinLeer.length > 0) {
        olvidar(espacio)
        void clienteDeConsultas.invalidateQueries({ queryKey: ['sitios', espacio] })
        setEspera(pendiente)
        setCreando(null)
        return
      }
      await terminar(pendiente)
    } catch (e) {
      const fallo = e as ErrorDeLaApi
      if (sitio !== null) {
        // La web ya existe: la pantalla de progreso sabe qué hacer con un alta a medias.
        irAlProgreso(sitio, { avisos: [...avisos, mensajeDelFallo(fallo)] })
        return
      }
      // El mensaje bueno, aunque venga dentro de `data.errors`, y de vuelta al paso donde se corrige.
      const codigo = (fallo.datos as { codigo?: unknown } | undefined)?.codigo
      setError({
        texto: mensajeDelFallo(fallo),
        ...(codigo === 'subir-de-plan' || codigo === 'tope-de-sitios' ? { enlace: { href: `/e/${espacio}/creditos`, etiqueta: 'Ver mi plan' } } : {}),
      })
      const volverA = pasoDelCampo(campoDelFallo(fallo))
      if (volverA) setPaso(volverA)
      setCreando(null)
    }
  }

  if (espera) {
    const actual = espera
    return (
      <>
        <Titulo
          descripcion="La web ya existe y todavía no se ha montado: falta que decidas qué hacer con lo que no se ha podido leer."
          volver={
            <Link className="mb-2 inline-flex items-center gap-1 text-[13px] text-tenue hover:text-tinta" to={`/e/${espacio}`}>
              <ArrowLeft className="size-3.5" /> Mis sitios web
            </Link>
          }
        >
          Crear una web
        </Titulo>
        <DocumentosSinLeer
          continuando={creando !== null}
          continuar={() => void terminar(actual)}
          documentos={actual.documentos}
          leer={leerOtraVez}
          quitar={(id) => setEspera((e) => (e ? sinDocumento(e, id) : e))}
        />
        {creando && (
          <p className="mt-3 text-[13px] text-tenue" role="status">
            {creando}
          </p>
        )}
      </>
    )
  }

  return (
    <>
      <Titulo
        descripcion="Elige qué quieres y danos tus materiales: la plataforma monta la web con su estructura, y tú la revisas antes de publicar."
        volver={
          <Link className="mb-2 inline-flex items-center gap-1 text-[13px] text-tenue hover:text-tinta" to={`/e/${espacio}`}>
            <ArrowLeft className="size-3.5" /> Mis sitios web
          </Link>
        }
      >
        Crear una web
      </Titulo>

      <ol className="mb-6 flex flex-wrap gap-2" aria-label="Pasos del asistente">
        {pasos.map((p, i) => {
          const hecho = i < indice
          const actual = p === paso
          return (
            <li key={p}>
              <button
                aria-current={actual ? 'step' : undefined}
                className={clases(
                  'flex items-center gap-2 rounded-pastilla border px-3 py-1.5 text-[13px] font-medium transition-colors',
                  actual ? 'border-violeta bg-violeta-suave text-violeta' : hecho ? 'border-linea bg-papel text-tinta hover:border-linea-fuerte' : 'border-linea bg-papel-2 text-tenue',
                )}
                disabled={!hecho && !actual}
                onClick={() => hecho && setPaso(p)}
                type="button"
              >
                <span className={clases('flex size-5 items-center justify-center rounded-full text-xs', actual ? 'bg-violeta text-white' : hecho ? 'bg-ok-suave text-ok' : 'bg-papel-3 text-tenue')}>{hecho ? <Check className="size-3" /> : i + 1}</span>
                {ROTULOS[p]}
              </button>
            </li>
          )
        })}
      </ol>

      {recuperado && !conFicheros && (paso === 'marca' || paso === 'materiales' || paso === 'productos' || paso === 'resumen') && (
        <div className="mb-4">
          <Aviso tono="info">Hemos recuperado lo que escribiste la última vez. Los ficheros —logotipo, imágenes, documentos y el Excel— no se pueden guardar en el navegador: si los habías elegido, vuelve a elegirlos.</Aviso>
        </div>
      )}

      {error && (
        <div className="mb-4">
          <Aviso tono="error">
            {error.texto}
            {error.enlace && (
              <>
                {' '}
                <Link className="font-medium underline" to={error.enlace.href}>
                  {error.enlace.etiqueta}
                </Link>
              </>
            )}
          </Aviso>
        </div>
      )}

      {paso === 'tipo' && <PasoTipo cambiar={cambiar} datos={datos} planConTienda={planConTienda} />}
      {paso === 'plantilla' && <PasoPlantilla cambiar={cambiar} cargando={cargandoPlantillas} datos={datos} plantillas={plantillas ?? []} />}
      {paso === 'marca' && <PasoMarca cambiar={cambiar} cambiarFicheros={cambiarFicheros} datos={datos} ficheros={ficheros} />}
      {paso === 'materiales' && <PasoMateriales cambiar={cambiar} cambiarFicheros={cambiarFicheros} datos={datos} ficheros={ficheros} plantilla={plantilla} />}
      {paso === 'paginas' && <PasoPaginas cambiar={cambiar} datos={datos} plantilla={plantilla} />}
      {paso === 'productos' && (
        <PasoProductos
          analizar={(f) => void analizarProductos(f)}
          cambiarProductos={setProductos}
          error={errorDeProductos}
          ocupado={analizando}
          productos={productos}
          urlDeLaPlantilla={urlDeLaPlantillaDelEspacio(espacio, datos.tienda.moneda)}
        />
      )}
      {paso === 'resumen' && <PasoResumen datos={datos} ficheros={ficheros} plantilla={plantilla} productos={datos.tipo === 'tienda' ? productos : null} saldo={saldo?.total ?? null} tipo={datos.tipo} />}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-linea pt-4">
        <Boton disabled={indice === 0 || creando !== null} icono={<ArrowLeft />} onClick={atras}>
          Atrás
        </Boton>
        <div className="flex items-center gap-3">
          {creando && (
            <span className="text-[13px] text-tenue" role="status">
              {creando}
            </span>
          )}
          {paso === 'resumen' ? (
            <Boton cargando={creando !== null} icono={<Sparkles />} onClick={() => void crear()} tipo="primario">
              Crear mi web
            </Boton>
          ) : (
            <Boton disabled={!puedeSeguir[paso]} icono={<ArrowRight />} onClick={siguiente} tipo="primario">
              Continuar
            </Boton>
          )}
        </div>
      </div>
    </>
  )
}
